import { unByKey } from 'ol/Observable';
import ImageLayer from 'ol/layer/Image';
import GroupLayer from 'ol/layer/Group';

import { SourceProperty } from 'cadenza/map/source-property';
import { refreshLayer } from 'cadenza/map/layer-utils';
import { getLayerTasks } from 'cadenza/map/layer-tasks';
import { LayerProperty } from 'cadenza/map/layer-property';
import { context } from 'cadenza/map/context/context';
import type { Map } from 'cadenza/map/map';
import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege';

import type BaseLayer from 'ol/layer/Base';
import type { EventsKey } from 'ol/events';
import type { ImageMapGuide } from 'ol/source';
import type ImageArcGISRestSource from 'ol/source/ImageArcGISRest';
import type { ImageSourceEvent } from 'ol/source/Image';

/**
 * Please do *not* instantiate this directly and instead use cadenza/map/context, i.e. context(layer)
 */
export class LayerContext {

  readonly _layer: BaseLayer;
  _imgLoadTaskId?: string;
  _imageLoadTaskRegistered = false;
  _eventKeys?: EventsKey[];
  _intervalId?: number;

  constructor (layer: BaseLayer) {
    this._layer = layer;
  }

  setup (map: Map) {
    const layer = this._layer;
    this._eventKeys = [
      layer.on(`change:${LayerProperty.VISIBLE}` as never, () => context(map).push())
    ];
    const interval = layer.get(LayerProperty.RELOAD_INTERVAL);
    if (interval > 0) {
      this._intervalId = window.setInterval(() => refreshLayer(layer), interval);
    }
  }

  teardown () {
    unByKey(this._eventKeys!);
    clearInterval(this._intervalId);
    this._cancelImageLoading();

    if (this._layer instanceof GroupLayer) {
      this._layer.getLayers().forEach(layer => context(layer).teardown());
    }
  }

  hasPrivilege (privilege: ObjectPrivilege) {
    return this._privileges.includes(privilege);
  }

  get _privileges () {
    return this._layer.get('privileges') || [];
  }

  // see ol/layer/Layer#visibleAtResolution()
  isVisibleAtResolution (resolution: number) {
    return this._isVisibleAtResolution(this._layer, resolution);
  }

  _isVisibleAtResolution (layer: BaseLayer, resolution: number) {
    if (!layer.getVisible()) {
      return false;
    }
    if (layer instanceof GroupLayer) {
      let isVisible = false as boolean;
      layer.getLayers().forEach(subLayer => {
        isVisible = isVisible || this._isVisibleAtResolution(subLayer, resolution);
      });
      return isVisible;
    }
    const { minResolution, maxResolution } = layer.getProperties();
    return isVisibleAtResolution(resolution, minResolution, maxResolution);
  }

  getInRange (resolution: number) {
    return this._isInRange(this._layer, resolution);
  }

  _isInRange (layer: BaseLayer, resolution: number) {
    if (layer instanceof GroupLayer) {
      const subLayers = layer.getLayers();
      if (!subLayers.getLength()) {
        // empty groups are considered in range
        return true;
      }
      let inRange = false;
      subLayers.forEach(subLayer => {
        inRange = inRange || this._isInRange(subLayer, resolution);
      });
      return inRange;
    }
    const { minResolution, maxResolution } = layer.getProperties();
    return (resolution >= minResolution && resolution < maxResolution);
  }

  registerImageLoadTaskHandling () {
    if (!(this._layer instanceof ImageLayer)) {
      return;
    }

    const imgLoadExecutionHelper = this._getImgLoadExecutionHelper();
    if (imgLoadExecutionHelper && !this._imageLoadTaskRegistered) {
      this._imageLoadTaskRegistered = true;
      const source = (this._layer as ImageLayer<ImageMapGuide | ImageArcGISRestSource>).getSource()!;
      const layerTasks = getLayerTasks(this._layer);
      // retrieve loading task id from image element
      // see layer-utils.js -> createImageLoadFunction
      const retrieveTaskIdFromImageSourceEvent = (event: ImageSourceEvent) => {
        const image = event.image.getImage();
        if (image instanceof HTMLElement && image.dataset.taskId) {
          return image.dataset.taskId;
        }
        // fallback for external layer
        return source.get(SourceProperty.CURRENT_LAODING_TASK_ID);
      };
      source.on('imageloadstart', () => {
        layerTasks.completeTask(source.get(SourceProperty.CURRENT_LAODING_TASK_ID));
        // pass loading taskId which is used for backend cancelId and is set in image.dataset.taskId
        // see layer-utils.js -> createImageLoadFunction
        source.set(
          SourceProperty.CURRENT_LAODING_TASK_ID,
          layerTasks.registerTask(
            () => imgLoadExecutionHelper.cancelCurrent(),
            () => refreshLayer(this._layer)
          ));
      });
      source.on('imageloadend', (event: ImageSourceEvent) => {
        source.set(SourceProperty.MESSAGES, []);
        const taskId = retrieveTaskIdFromImageSourceEvent(event);
        layerTasks.completeTask(taskId);
      });
      source.on('imageloaderror', (event: ImageSourceEvent) => {
        source.set(SourceProperty.MESSAGES, [
          { type: 'ERROR', code: 'layer.errors.data.loadingFailed', args: { message: '' } }
        ]);
        const taskId = retrieveTaskIdFromImageSourceEvent(event);
        layerTasks.completeTask(taskId);
      });
    }
  }

  _cancelImageLoading () {
    this._getImgLoadExecutionHelper()?.cancelCurrent(); // Helper only available on image sources
  }

  restartTasks () {
    getLayerTasks(this._layer).restartTasks();
  }

  _getImgLoadExecutionHelper () {
    return this._layer.get(LayerProperty.IMG_LOAD_EXECUTION_HELPER);
  }

  completeAllTasks () {
    getLayerTasks(this._layer).completeAllTasks();
  }

}

export function isVisibleAtResolution (resolution: number, minResolution: number, maxResolution: number) {
  return (minResolution <= resolution && resolution < maxResolution);
}
